import { db, getActiveStoreId } from './db';
import { WooCommerceAPI, loadConfig, type WooCommerceConfig } from './woocommerce';
import { toast } from 'sonner';
import { addAppLog } from "./logger";

export class SyncService {
  public static isSyncing = false;

  /**
   * Request storage persistence from the browser
   */
  static async requestPersistence() {
    if (typeof window === 'undefined') return false;
    if (navigator.storage && navigator.storage.persist) {
      const isPersisted = await navigator.storage.persist();
      console.log(`[SyncService] Storage persisted: ${isPersisted}`);
      return isPersisted;
    }
    return false;
  }

  /**
   * Validates and attaches storeId to items before DB storage
   */
  private static validateItems(items: any[], storeId: string) {
    if (!Array.isArray(items)) return [];
    return items
      .filter(item => item && (item.id !== undefined || item.key !== undefined) && storeId)
      .map(item => ({ ...item, storeId }));
  }

  static async forceSync(onProgress?: (msg: string) => void, silent: boolean = false) {
    if (this.isSyncing) return;

    const config = loadConfig();
    const isNative = config?.connectionType === 'native';
    // GUARD: If no valid config, return immediately WITHOUT setting isSyncing.
    // Native stores have no url/consumerKey — the backend is the source.
    if (isNative ? !(config?.nativeStoreId || (config as any)?.id) : (!config?.url || config.url === 'https://' || !config?.consumerKey)) {
      console.warn('[SyncService] forceSync aborted: no valid config');
      return;
    }

    this.isSyncing = true;
    const storeId = getActiveStoreId();
    const log = (msg: string) => {
      if (!silent) console.log(`[SyncService] ${msg}`);
      if (onProgress) onProgress(msg);
    };

    try {
      if (!silent) log("Iniciando sincronização total...");
      
      // 1. Categories
      log("Sincronizando categorias...");
      const categories = await WooCommerceAPI.getCategories(config);
      await db.categories.where('storeId').equals(storeId).delete();
      await db.categories.bulkPut(this.validateItems(categories, storeId));

      // 2. Brands (optional — unsupported on native stores)
      try {
        log("Sincronizando marcas...");
        const brands = await WooCommerceAPI.getBrands(config);
        await db.brands.where('storeId').equals(storeId).delete();
        await db.brands.bulkPut(this.validateItems(brands, storeId));
      } catch (e) { console.warn('[SyncService] Brands sync skipped:', e); }

      // 3. Attributes (optional — unsupported on native stores)
      try {
        log("Sincronizando atributos...");
        const attrs = await WooCommerceAPI.getAttributes(config);
        await db.attributes.where('storeId').equals(storeId).delete();
        const validAttrs = this.validateItems(attrs, storeId);
        await db.attributes.bulkPut(validAttrs);

        for (const attr of validAttrs) {
          const terms = await WooCommerceAPI.getAttributeTerms(config, attr.id);
          await db.attributeTerms.where({ storeId, attributeId: attr.id }).delete();
          await db.attributeTerms.bulkPut(this.validateItems(terms, storeId).map(t => ({ ...t, attributeId: attr.id })));
        }
      } catch (e) { console.warn('[SyncService] Attributes sync skipped:', e); }

      // 4. Products (All)
      log("Sincronizando catálogo completo...");
      let page = 1;
      let hasMore = true;
      let totalProds = 0;
      await db.products.where('storeId').equals(storeId).delete();
      
      while (hasMore && page <= 50) { // Increased to 50 pages (5000 products)
        const prods = await WooCommerceAPI.getProducts(config, { page, per_page: 100 });
        if (Array.isArray(prods) && prods.length > 0) {
          const valid = this.validateItems(prods, storeId);
          await db.products.bulkPut(valid);
          totalProds += valid.length;
          const totalPages = (prods as any).totalPages || 1;
          
          // Better detection of "no more products"
          if (page >= totalPages && prods.length < 100) {
            hasMore = false;
          } else if (prods.length < 100) {
             hasMore = false;
          }
          
          page++;
          // Fetch variations for variable products in this batch (limit variations to avoid 429)
          const variablePids = valid.filter(p => p.type === 'variable').map(p => p.id);
          if (variablePids.length > 0) {
             log(`Buscando variações para ${variablePids.length} produtos em background...`);
             this.syncVariations(config, variablePids).catch(e => console.error("[SyncService] Variation sync error", e));
          }
        } else {
          hasMore = false;
        }
      }

      // 5. Orders (Last 500)
      log("Sincronizando encomendas recentes...");
      for(let op=1; op<=5; op++) {
        const orders = await WooCommerceAPI.getOrders(config, { page: op, per_page: 100 });
        if (Array.isArray(orders) && orders.length > 0) {
          await db.orders.bulkPut(this.validateItems(orders, storeId));
          if (orders.length < 100) break;
        } else break;
      }

      // 6. Customers
      log("Sincronizando clientes...");
      const customers = await WooCommerceAPI.getCustomers(config, { per_page: 100 });
      await db.customers.where('storeId').equals(storeId).delete();
      await db.customers.bulkPut(this.validateItems(customers, storeId));

      // 7. Gallery (All) — WordPress media library doesn't exist on native
      // stores (they use Bunny CDN via the gallery view), so skip it there.
      if (!isNative) try {
        log("Sincronizando biblioteca de média...");
        let mPage = 1;
        let mHasMore = true;
        await db.gallery.where('storeId').equals(storeId).delete();
        const MAX_PAGES = 200; // safety cap
        while (mHasMore && mPage <= MAX_PAGES) {
          log(`Sincronizando média - Página ${mPage}...`);
          const media = await WooCommerceAPI.getWPFiles(config, { page: mPage, per_page: 100 });
          if (Array.isArray(media) && media.length > 0) {
            await db.gallery.bulkPut(this.validateItems(media, storeId));

            // Better logic: if we received exactly 100 items, always try next page regardless of header
            if (media.length < 100) {
              mHasMore = false;
            }

            mPage++;
            // Small delay to avoid rate limiting
            if (mPage % 2 === 0) await new Promise(r => setTimeout(r, 400));
          } else {
            mHasMore = false;
          }
        }
      } catch (e) { console.warn('[SyncService] Gallery sync skipped:', e); }

      await this.updateMetadata('all', storeId);
      log("Sincronização concluída com sucesso!");
      window.dispatchEvent(new CustomEvent('sync-completed'));
      window.dispatchEvent(new CustomEvent('dashboard-refresh'));
    } catch (e: any) {
      console.error("Force sync failed", e);
      addAppLog('error', 'Sync', `Erro na sincronização: ${e.message}`);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Sync variations for a list of product IDs
   */
  private static async syncVariations(config: WooCommerceConfig, productIds: number[]) {
    const storeId = getActiveStoreId();
    // Batch processing with delay
    for (const pid of productIds) {
      try {
        await new Promise(r => setTimeout(r, 800)); // Reduced delay to 800ms
        const variations = await WooCommerceAPI.getVariations(config, pid);
        const validVars = this.validateItems(variations, storeId).map(v => ({ ...v, productId: pid }));
        if (validVars.length > 0) {
          await db.variations.bulkPut(validVars);
        }
      } catch (e) {
        console.warn(`[SyncService] Failed to sync variations for product ${pid}`, e);
      }
    }
  }

  /**
   * Coordinated preload for all tabs (Products, Orders, Customers, etc.)
   */
  static async preloadAllTabs(storeId: string) {
    if (this.isSyncing) return;
    
    const config = loadConfig();
    const isNative = config?.connectionType === 'native';
    // GUARD: If no valid config, abort cleanly
    if (isNative ? !(config?.nativeStoreId || (config as any)?.id) : (!config?.url || config.url === 'https://' || !config?.consumerKey)) {
      console.warn('[SyncService] preloadAllTabs aborted: no valid config');
      return;
    }

    this.isSyncing = true;
    const log = (msg: string) => console.log(`[SyncService:Preload] ${msg}`);

    try {
      log(`Starting coordinated preload for store: ${storeId}`);
      
      // Batch 1: High priority data
      const [productsRes, ordersRes, customersRes] = await Promise.allSettled([
        WooCommerceAPI.getProducts(config, { per_page: 50 }),
        WooCommerceAPI.getOrders(config, { per_page: 50 }),
        WooCommerceAPI.getCustomers(config, { per_page: 50 })
      ]);

      // 1. Process Products
      if (productsRes.status === 'fulfilled' && Array.isArray(productsRes.value)) {
        const valid = this.validateItems(productsRes.value, storeId);
        if (valid.length > 0) {
          await db.products.bulkPut(valid);
          await this.updateMetadata('products', storeId);
        }
      }

      // 2. Process Orders
      if (ordersRes.status === 'fulfilled' && Array.isArray(ordersRes.value)) {
        const valid = this.validateItems(ordersRes.value, storeId);
        if (valid.length > 0) {
          await db.orders.bulkPut(valid);
          await this.updateMetadata('orders', storeId);
        }
      }

      // 3. Process Customers
      if (customersRes.status === 'fulfilled' && Array.isArray(customersRes.value)) {
        const valid = this.validateItems(customersRes.value, storeId);
        if (valid.length > 0) {
          await db.customers.bulkPut(valid);
          await this.updateMetadata('customers', storeId);
        }
      }

      // Batch 2: Lower priority metadata
      const [categoriesRes, brandsRes, attributesRes] = await Promise.allSettled([
        WooCommerceAPI.getCategories(config),
        WooCommerceAPI.getBrands(config),
        WooCommerceAPI.getAttributes(config)
      ]);

      // 4. Process Categories
      if (categoriesRes.status === 'fulfilled' && Array.isArray(categoriesRes.value)) {
        const valid = this.validateItems(categoriesRes.value, storeId);
        if (valid.length > 0) {
          await db.categories.bulkPut(valid);
          await this.updateMetadata('categories', storeId);
        }
      }

      // 5. Process Brands
      if (brandsRes.status === 'fulfilled' && Array.isArray(brandsRes.value)) {
        const valid = this.validateItems(brandsRes.value, storeId);
        if (valid.length > 0) {
          await db.brands.bulkPut(valid);
          await this.updateMetadata('brands', storeId);
        }
      }

      // 6. Process Attributes
      if (attributesRes.status === 'fulfilled' && Array.isArray(attributesRes.value)) {
        const valid = this.validateItems(attributesRes.value, storeId);
        if (valid.length > 0) {
          await db.attributes.bulkPut(valid);
          await this.updateMetadata('attributes', storeId);
        }
      }

      log("Preload completed successfully");
      addAppLog('success', 'Coordinated Sync', `All tabs preloaded successfully for store ${storeId}`);
      window.dispatchEvent(new CustomEvent('store-data-synced', { detail: storeId }));
      window.dispatchEvent(new CustomEvent('sync-completed'));
      
    } catch (error: any) {
      log(`Preload failed: ${error.message}`);
      addAppLog('error', 'Preload Failed', `Error: ${error.message}`);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Update sync metadata timestamp
   */
  private static async updateMetadata(key: string, storeId: string) {
    if (!key || !storeId) return;
    const timestamp = Date.now();
    try {
      await db.syncMetadata.put({
        key,
        storeId,
        timestamp
      });
      localStorage.setItem(`wc_${storeId}_${key}_last_sync`, timestamp.toString());
      if (key === 'all' || key === 'dashboard') {
        localStorage.setItem(`wc_${storeId}_last_sync_timestamp`, timestamp.toString());
      }
    } catch (e) {
      console.error("[SyncService] Failed to update metadata", e);
    }
  }

  /**
   * Background sync logic - checks for updates silently
   */
  static async backgroundSync() {
    if (this.isSyncing) return;
    const config = loadConfig();

    // Safety check: Don't sync if config is missing or has placeholder URL.
    // Native stores have no Woo credentials — the backend serves them.
    const isValidConfig = config?.connectionType === 'native'
      ? !!(config?.nativeStoreId || (config as any)?.id)
      : config?.url &&
        config.url !== 'https://' &&
        config.url !== 'http://' &&
        !config.url.includes('wp-json') &&
        config.consumerKey &&
        config.consumerSecret;

    if (!isValidConfig) {
      console.log("[SyncService] Background sync skipped: No valid WooCommerce config.");
      return;
    }

    const storeId = getActiveStoreId();
    
    try {
      // Check if we have any products for this store
      const count = await db.products.where('storeId').equals(storeId).count();
      const perPage = count === 0 ? 100 : 20; // Fetch more if initial sync
      
      console.log(`[SyncService] Starting background sync for ${storeId} (existing: ${count}, fetching: ${perPage})`);
      
      const latest = await WooCommerceAPI.getProducts(config, { 
        per_page: perPage, 
        orderby: 'modified', 
        order: 'desc' 
      });
      
      const validLatest = this.validateItems(latest, storeId);
      if (validLatest.length > 0) {
        await db.products.bulkPut(validLatest);
        await this.updateMetadata('products', storeId);
        
        console.log(`[SyncService] Background sync updated ${validLatest.length} products for ${storeId}`);
        window.dispatchEvent(new CustomEvent('products-updated'));
      }
    } catch (e: any) {
      if (e.isConfigError) {
        console.log("[SyncService] Background sync postponed: Missing configuration.");
      } else if (e instanceof TypeError && e.message.includes('Load failed')) {
        // Suppress scary console logs for common network failures during background sync
        console.log("[SyncService] Background sync skipped: Network connection unstable.");
      } else {
        console.warn("[SyncService] Background sync failed", e.message || e);
      }
    }
  }

  /**
   * Initialize service: Persistence and Initial Sync check
   */
  static async init() {
    await this.requestPersistence();
    
    const storeId = getActiveStoreId();
    if (!storeId || localStorage.getItem('app_logged_in') !== 'true') return;

    try {
      const meta = await db.syncMetadata.get(['products', storeId]);
      
      // If no data ever synced, or cache is older than 2 hours, or no products found
      const isExpired = !meta || (Date.now() - meta.timestamp > 7200000);
      const productCount = await db.products.where('storeId').equals(storeId).count();
      
      if (isExpired || productCount === 0) {
        console.log(`[SyncService] Initial preload triggered for ${storeId} (Expired: ${isExpired}, Count: ${productCount})`);
        this.preloadAllTabs(storeId);
      } else {
        // Just a light background sync if cache is relatively fresh
        this.backgroundSync();
      }
    } catch (e) {
      console.error("[SyncService] Init check failed", e);
    }
  }
}
